
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { SEO } from "@/components/SEO";

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-black text-white flex flex-col">
      <SEO 
        title="Privacy Policy - Slay"
        description="Read our Privacy Policy to understand how Slay collects, uses, and protects your personal information on our LGBTQIA+ event platform."
      />
      <Header />
      
      <main className="flex-grow pt-32 pb-16">
        <div className="container mx-auto px-4 max-w-4xl">
          <div className="text-center mb-12">
            <h1 className="text-4xl md:text-6xl font-bold bg-gradient-to-r from-[#9b87f5] to-[#D946EF] bg-clip-text text-transparent mb-6">
              Website Privacy Policy
            </h1>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              This Privacy Policy applies to all personal information collected by Slayverse Pty Ltd ABN 85 629 390 414 trading as Slay Events Australia.
            </p>
          </div>

          <div className="bg-gradient-to-br from-purple-900/20 via-black/40 to-pink-900/20 backdrop-blur-lg border border-white/10 rounded-2xl p-8 md:p-12 space-y-8">
            
            <p className="text-gray-300 leading-relaxed mb-8">
              This Privacy Policy applies to all personal information collected by <strong className="text-white">Slayverse Pty Ltd ABN 85 629 390 414</strong> trading as <strong className="text-white">Slay Events Australia</strong> (we, us or our) via the website located at{" "}
              <a href="https://slay.events/" className="text-purple-400 hover:text-purple-300 transition-colors">https://slay.events/</a>{" "}
              (Website).
            </p>

            <section className="space-y-4">
              <h2 className="text-2xl font-bold text-white border-b border-purple-500/30 pb-2">
                What information do we collect?
              </h2>
              <p className="text-gray-300 leading-relaxed">
                The kind of Personal Information that we collect from you will depend on how you use the website. The Personal Information which we collect and hold about you may include:
              </p>
              <p className="text-gray-300 leading-relaxed font-medium">
                login credentials, payment details, email address, name, phone number, address
              </p>
            </section>

            <section className="space-y-4">
              <h2 className="text-2xl font-bold text-white border-b border-purple-500/30 pb-2">
                Types of information
              </h2>
              <p className="text-gray-300 leading-relaxed">
                The Privacy Act 1998 (Cth) (Privacy Act) defines types of information, including Personal Information and Sensitive Information.
              </p>
              <p className="text-gray-300 leading-relaxed">
                <strong className="text-white">Personal Information</strong> means information or an opinion about an identified individual or an individual who is reasonably identifiable:
              </p>
              <ul className="list-disc list-inside text-gray-300 space-y-2 ml-4">
                <li>whether the information or opinion is true or not; and</li>
                <li>whether the information or opinion is recorded in a material form or not.</li>
              </ul>
              <p className="text-gray-300 leading-relaxed">
                If the information does not disclose your identity or enable your identity to be ascertained, it will in most cases not be classified as "Personal Information" and will not be subject to this privacy policy.
              </p>
              <p className="text-gray-300 leading-relaxed">
                <strong className="text-white">Sensitive Information</strong> is defined in the Privacy Act as including information or opinion about such things as an individual's racial or ethnic origin, political opinions, membership of a political association, religious or philosophical beliefs, membership of a trade union or other professional body, criminal record or health information.
              </p>
              <p className="text-gray-300 leading-relaxed">
                Sensitive Information will be used by us only:
              </p>
              <ul className="list-disc list-inside text-gray-300 space-y-2 ml-4">
                <li>for the primary purpose for which it was obtained;</li>
                <li>for a secondary purpose that is directly related to the primary purpose; and</li>
                <li>with your consent or where required or authorised by law.</li>
              </ul>
            </section>

            <section className="space-y-4">
              <h2 className="text-2xl font-bold text-white border-b border-purple-500/30 pb-2">
                How we collect your Personal Information
              </h2>
              <p className="text-gray-300 leading-relaxed">
                We may collect Personal Information from you whenever you input such information into the Website, related app or provide it to Us in any other way.
              </p>
              <p className="text-gray-300 leading-relaxed">
                We may also collect cookies from your computer which enable us to tell when you use the Website and also to help customise your Website experience. As a general rule, however, it is not possible to identify you personally from our use of cookies.
              </p>
              <p className="text-gray-300 leading-relaxed">
                We generally don't collect Sensitive Information, but when we do, we will comply with the preceding paragraph.
              </p>
              <p className="text-gray-300 leading-relaxed">
                Where reasonable and practicable we collect your Personal Information from you only. However, sometimes we may be given information from a third party, in cases like this we will take steps to make you aware of the information that was provided by a third party.
              </p>
            </section>

            <section className="space-y-4">
              <h2 className="text-2xl font-bold text-white border-b border-purple-500/30 pb-2">
                Purpose of collection
              </h2>
              <p className="text-gray-300 leading-relaxed">
                We collect Personal Information to provide you with the best service experience possible on the Website and keep in touch with you about developments in our business.
              </p>
              <p className="text-gray-300 leading-relaxed">
                We customarily only disclose Personal Information to our service providers who assist us in operating the Website. Your Personal Information may also be exposed from time to time to maintenance and support personnel acting in the normal course of their duties.
              </p>
              <p className="text-gray-300 leading-relaxed">
                By using our Website, you consent to the receipt of direct marketing material. We will only use your Personal Information for this purpose if we have collected such information direct from you, and if it is material of a type which you would reasonably expect to receive from use. We do not use sensitive Personal Information in direct marketing activity. Our direct marketing material will include a simple means by which you can request not to receive further communications of this nature, such as an unsubscribe button link.
              </p>
              <p className="text-gray-300 leading-relaxed">
                You can manage your marketing preferences through your account settings or by contacting our Privacy Officer. We will process opt-out requests within 5 business days and maintain records of your preferences. Marketing communications will not exceed 10 messages per month, and each communication will clearly display preference management options. If you choose to opt-out, we will retain minimal Personal Information necessary to ensure compliance with your request.
              </p>
            </section>

            <section className="space-y-4">
              <h2 className="text-2xl font-bold text-white border-b border-purple-500/30 pb-2">
                Security, Access and correction
              </h2>
              <p className="text-gray-300 leading-relaxed">
                We store your Personal Information in a way that reasonably protects it from unauthorised access, misuse, modification or disclosure. When we no longer require your Personal Information for the purpose for which we obtained in, we will take reasonable steps to destroy and anonymise or de-identify it. Most of the Personal Information that is stored in our client files and records will be kept for a maximum of 7 years to fulfill our record keeping obligations.
              </p>
              <p className="text-gray-300 leading-relaxed">
                We implement industry-standard security measures including encryption, access controls, and secure data centers to protect your Personal Information. When deletion is required, we use secure erasure methods including digital shredding and physical destruction of storage media. For digital records, we employ 60 day retention periods for active data and 7 years for archived data, after which automated purge protocols permanently remove the information using government-approved secure deletion standards.
              </p>
              <p className="text-gray-300 leading-relaxed">
                The Australian Privacy Principles:
              </p>
              <ul className="list-disc list-inside text-gray-300 space-y-2 ml-4">
                <li>permit you to obtain access to the Personal Information we hold about you in certain circumstances (Australian Privacy Principle 12); and</li>
                <li>allow you to correct inaccurate Personal Information subject to certain exceptions (Australian Privacy Principle 13).</li>
              </ul>
              <p className="text-gray-300 leading-relaxed">
                Where you would like to obtain such access, please contact us in writing on the contact details set out at the bottom of this privacy policy.
              </p>
            </section>

            <section className="space-y-4">
              <h2 className="text-2xl font-bold text-white border-b border-purple-500/30 pb-2">
                Complaint procedure
              </h2>
              <p className="text-gray-300 leading-relaxed">
                If you have a complaint concerning the manner in which we maintain the privacy of your Personal Information, please contact us as on the contact details set out at the bottom of this policy. All complaints will be considered by Lachlan Matheson and we may seek further information from you to clarify your concerns. If we agree that your complaint is well founded, we will, in consultation with you, take appropriate steps to rectify the problem. If you remain dissatisfied with the outcome, you may refer the matter to the Office of the Australian Information Commissioner.
              </p>
              <h3 className="text-lg font-semibold text-white mt-4">
                Documentation and Response Timeline
              </h3>
              <p className="text-gray-300 leading-relaxed">
                We will acknowledge receipt of your complaint within 5 business days and provide you with a reference number. Our privacy team will investigate your complaint and maintain detailed records of all communications and findings. We aim to resolve all privacy complaints within 20 business days. If additional time is required, we will notify you in writing. All complaint documentation will be retained for 12 months following resolution. If the matter requires escalation, our Privacy Officer will personally review your case within 5 business days of the escalation request.
              </p>
            </section>

            <section className="space-y-4">
              <h2 className="text-2xl font-bold text-white border-b border-purple-500/30 pb-2">
                Overseas transfer
              </h2>
              <p className="text-gray-300 leading-relaxed">
                Your Personal Information may be transferred to recipients located in United States of America and Europe. United States of America and Europe has data protection laws which protect Personal Information in a way which is at least substantially similar to the Australian Privacy Principles, and there will be mechanisms available to you to enforce protection of your Personal Information under that overseas law. In the circumstances, we do not require the overseas recipients to comply with the Australian Privacy Principles and we will not be liable for a breach of the Australian Privacy Principles if your Personal Information is mishandled.
              </p>
            </section>

            <section className="space-y-4">
              <h2 className="text-2xl font-bold text-white border-b border-purple-500/30 pb-2">
                GDPR
              </h2>
              <p className="text-gray-300 leading-relaxed">
                In some circumstances, the European Union General Data Protection Regulation (GDPR) provides additional protection to individuals located in Europe. The fact that you may be located in Europe does not, however, on its own entitle you to protection under the GDPR. Our website does not specifically target customers located in the European Union and we do not monitor the behaviour of individuals in the European Union, and accordingly the GDPR does not apply.
              </p>
            </section>

            <section className="space-y-4">
              <h2 className="text-2xl font-bold text-white border-b border-purple-500/30 pb-2">
                How to contact us about privacy
              </h2>
              <p className="text-gray-300 leading-relaxed">
                If you have any queries, or if you seek access to your Personal Information, or if you have a complaint about our privacy practices, you can contact us through:
              </p>
              <div className="bg-black/30 rounded-lg p-4 border border-purple-500/20">
                <p className="text-white">Email: support@slay.events</p>
              </div>
            </section>

            <div className="text-center pt-8 border-t border-purple-500/30">
              <p className="text-gray-400">
                Last updated: {new Date().toLocaleDateString('en-AU', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}
              </p>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default PrivacyPolicy;
